/**
 * About / version card for Settings → About. Uses the settings `FormCard`
 * shell + semantic tone tokens. Shows the founder-relevant build details:
 * version, build reference, last updated, and environment.
 */
import { Info } from "lucide-react";
import { cn } from "@flint/ui";
import { FormCard } from "../form-card.tsx";
import { formatDateTime } from "../../../lib/format.ts";

export interface BuildInfo {
  version: string;
  gitSha: string;
  builtAt: string | null;
  environment: string;
  runtime: string;
}

/** Environment → pip tone, reusing the rail's tone families. */
const ENV_PIP: Record<string, string> = {
  production: "bg-ok-50 text-ok border-ok-100",
  staging: "bg-warn-50 text-warn border-warn-100",
  development: "bg-sky-50 text-sky-700 border-sky-100",
  test: "bg-sky-50 text-sky-700 border-sky-100",
};

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b border-line px-5 py-2.5 last:border-b-0">
      <span className="text-xs text-muted">{label}</span>
      <span className="text-xs font-medium text-ink tabular-nums">{children}</span>
    </div>
  );
}

export function AboutCard({ info }: { info: BuildInfo }) {
  return (
    <FormCard
      icon={<Info size={14} strokeWidth={2} aria-hidden />}
      title="Flint"
      description="Founder’s Clarity ERP"
      bodyClassName="p-0"
    >
      <Row label="Version">{info.version}</Row>
      <Row label="Build">
        <code className="rounded-sm bg-bg px-1.5 py-0.5 font-mono text-xs text-ink-2">{info.gitSha}</code>
      </Row>
      <Row label="Last updated">{info.builtAt ? formatDateTime(info.builtAt) : "—"}</Row>
      <Row label="Environment">
        <span
          className={cn(
            "rounded-sm border px-1.5 py-px text-xs font-semibold uppercase tracking-wider",
            ENV_PIP[info.environment] ?? "bg-bg text-ink-2 border-line",
          )}
        >
          {info.environment}
        </span>
      </Row>
      <div className="flex flex-wrap gap-x-4 gap-y-1 px-5 py-3 text-xs text-sky-700">
        <a href="/legal/terms" className="hover:underline">Terms</a>
        <a href="/legal/privacy" className="hover:underline">Privacy</a>
        <a href="mailto:support@flowtrail.ai" className="hover:underline">Support</a>
        <a href="/changelog" className="hover:underline">Changelog</a>
      </div>
    </FormCard>
  );
}
